/**
 * AdminPage — /admin (V9.20 UX refactor)
 *
 * Tableau de bord central Admin AITO.PF. Regroupe toutes les fonctions
 * administratives en cartes cliquables organisées par section, avec des
 * compteurs "à traiter" quand pertinents.
 *
 * Groupes :
 *   1. À traiter aujourd'hui (cartes actionnables avec badges)
 *   2. Gestion des personnes & structures
 *   3. Contenus & éditorial
 *   4. Outils techniques & maintenance
 *
 * Les textes légaux sont accessibles via un onglet dédié en bas de page.
 */
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { adminMailto, getAdminEmail } from "@/lib/adminContact";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { showError } from "@/lib/errors";
import {
  ShieldAlert, Gavel, ArrowRight, ShieldCheck, Trash2, Star, Newspaper,
  FileSpreadsheet, Copy, GitMerge, Shirt, BookOpen, Users, Building2,
  Flag, HandCoins, Settings, Wand2, ClipboardCheck, Sparkles, Layers,
  Store, Handshake, Crown, Trophy, Globe, FileJson,
} from "lucide-react";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// Configuration des cartes
// ---------------------------------------------------------------------------
// Chaque carte = { to, icon, title, desc, color, badgeKey, adminOnly, testid }
// `badgeKey` = clé dans /api/admin/stats/pending pour afficher un compteur.

const CARDS_TODO = [
  {
    to: "/admin/moderation", icon: Gavel,
    title: "Modération",
    desc: "Dépublier, archiver, restaurer les entités signalées ou à valider",
    color: "hsl(var(--aito-lagoon))",
    badgeKey: "moderation",
    testid: "admin-card-moderation",
  },
  {
    to: "/admin/memoires", icon: BookOpen,
    title: "Atelier Livrets AITO",
    desc: "Demandes reçues, snapshot déterministe, rédaction, agrément COPF",
    color: "hsl(var(--aito-gold))",
    badgeKey: "memoires",
    testid: "admin-card-memoires",
  },
  {
    to: "/admin/revendications", icon: ShieldCheck,
    title: "Revendications de gestion",
    desc: "Demandes de gestion Club / Fédération à valider",
    color: "hsl(var(--aito-lagoon))",
    badgeKey: "revendications",
    testid: "admin-card-revendications",
  },
  {
    to: "/admin/contenus", icon: Newspaper,
    title: "Cockpit Contenus",
    desc: "Veille curator, articles contributeurs, revue de presse — un seul écran unifié",
    color: "hsl(var(--aito-lagoon))",
    badgeKey: "contenus",
    testid: "admin-card-contenus",
  },
  {
    to: "/admin/news-curator", icon: FileJson,
    title: "Curator IA — Actualités sportives",
    desc: "Importe un bloc JSON préparé par ChatGPT. Création + mise à la Une en un clic.",
    color: "hsl(var(--aito-lagoon))",
    testid: "admin-card-news-curator",
  },
  {
    to: "/admin/funding", icon: HandCoins,
    title: "Aides sportives",
    desc: "Aides publiques et privées à valider et publier",
    color: "hsl(var(--aito-energy))",
    badgeKey: "aides",
    testid: "admin-card-aides",
  },
  {
    to: "/admin/athletes/duplicates", icon: Copy,
    title: "Doublons athlètes",
    desc: "Rapprochements probables issus des imports",
    color: "hsl(var(--aito-energy))",
    badgeKey: "doublons",
    testid: "admin-card-duplicates",
  },
  // V13.15 — Files d'attente validation palmarès (COPF + Fédération)
  {
    to: "/copf/validations", icon: Crown,
    title: "Validations palmarès · COPF",
    desc: "Fiches d'athlètes en attente de validation officielle par le COPF",
    color: "hsl(var(--aito-gold))",
    badgeKey: "copf_validations",
    testid: "admin-card-copf-validations",
  },
  {
    to: "/federation/validations", icon: ShieldCheck,
    title: "Validations palmarès · Fédérations",
    desc: "Fiches d'athlètes en attente de validation par leur fédération d'affiliation",
    color: "hsl(var(--aito-lagoon))",
    badgeKey: "fed_validations",
    testid: "admin-card-fed-validations",
  },
];

const CARDS_ENTITIES = [
  {
    to: "/admin/moderation?entity=athlete", icon: Users,
    title: "Gérer les Athlètes",
    desc: "Liste admin — publier, dépublier, archiver, corriger",
    testid: "admin-card-athletes",
  },
  {
    to: "/admin/moderation?entity=coach", icon: ShieldCheck,
    title: "Gérer les Coachs",
    desc: "Liste admin — publier, dépublier, archiver",
    testid: "admin-card-coaches",
  },
  {
    to: "/admin/moderation?entity=club", icon: Building2,
    title: "Gérer les Clubs",
    desc: "Liste admin — publier, dépublier, archiver",
    testid: "admin-card-clubs",
  },
  {
    to: "/admin/moderation?entity=federation", icon: Flag,
    title: "Gérer les Fédérations",
    desc: "Liste admin — publier, dépublier, archiver",
    testid: "admin-card-federations",
  },
  {
    to: "/admin/entities", icon: Building2,
    title: "Créer fédération / club",
    desc: "Créer une nouvelle structure",
    testid: "admin-card-entities-create",
  },
  {
    to: "/admin/athletes/imports", icon: FileSpreadsheet,
    title: "Imports athlètes",
    desc: "Import massif XLSX/CSV, preview, doublons, revendications",
    testid: "admin-card-imports",
  },
];

const CARDS_CONTENTS = [
  // V13.14 — Fusion des 3 anciens modules (curator, sports-monitor, articles)
  // dans le cockpit unique `/admin/contenus`. On garde uniquement les modules
  // à identité éditoriale distincte (Mémoire du sport).
  {
    to: "/admin/memory", icon: Sparkles,
    title: "Mémoire du sport polynésien",
    desc: "Portraits et archives éditoriales",
    testid: "admin-card-memory",
  },
  {
    to: "/admin/memoire-sport", icon: BookOpen,
    title: "Mémoire du Sport — Bibliothèque",
    desc: "40 fiches patrimoniales · livres, archives vidéo, catalogues",
    testid: "admin-card-sport-memory",
  },
  // V13.7 — Fusionné dans /admin/games-selections (onglet "Tous les athlètes")
  {
    to: "/admin/directory", icon: Store,
    title: "Annuaire (Santé & Boutiques)",
    desc: "Gérer les fiches Santé & Performance et Boutiques & Services",
    testid: "admin-card-directory",
  },
  {
    to: "/admin/home-highlights", icon: Star,
    title: "Mise en avant Homepage",
    desc: "Zone verticale sous JP 2027 · Bande basse (2 emplacements)",
    testid: "admin-card-home-highlights",
  },
  {
    to: "/admin/site-settings", icon: Settings,
    title: "Paramètres du site",
    desc: "Date JP 2027 (24/07/2027) · Email admin contact · bannières",
    testid: "admin-card-site-settings",
  },
  {
    to: "/admin/hero-pin", icon: Star,
    title: "Hero athlète à la une",
    desc: "Épinglage temporel + classement automatique",
    testid: "admin-card-hero-pin",
  },
  {
    to: "/admin/featured-rotation", icon: Star,
    title: "Rotation UNE (hebdo)",
    desc: "Épingler / reléguer les Athlètes à la UNE pour une semaine",
    testid: "admin-card-featured-rotation",
  },
  {
    to: "/admin/campaigns", icon: Shirt,
    title: "Campagnes AITO x Athlète",
    desc: "Précommandes T-shirts (quantités par tranche 20/20) · aucun encaissement",
    testid: "admin-card-campaigns",
  },
  // V11.2 — Le module SupportCampaign V1 doublonnait avec Campagnes AITO x Athlète.
  // Décision produit : un seul module Campagnes T-shirts (avec quantités). La route
  // /admin/support-campaigns reste accessible directement pour maintenance.
  {
    to: "/admin/games-selections", icon: Flag,
    title: "Sélections JP 2027",
    desc: "Valider · refuser · révoquer les sélections officielles Polynésie",
    testid: "admin-card-games-selections",
  },
  {
    to: "/admin/games-import", icon: Trophy,
    title: "Import Jeux du Pacifique (historique)",
    desc: "Import Excel des résultats des éditions passées · matching auto · rollback",
    testid: "admin-card-games-import",
  },
  {
    to: "/admin/pacific-athletes", icon: Globe,
    title: "Athlètes du Pacifique",
    desc: "Consolider les identités documentaires étrangères · fusion des doublons",
    testid: "admin-card-pacific-athletes",
  },
];

const CARDS_TOOLS = [
  {
    to: "/admin/corbeille", icon: Trash2,
    title: "Corbeille",
    desc: "Restaurer ou purger les entités supprimées / archivées",
    testid: "admin-card-corbeille",
  },
  {
    to: "/admin/athletes/merge", icon: GitMerge,
    title: "Fusion d'athlètes",
    desc: "Fusion côte-à-côte, opération auditée (admin strict)",
    strictAdmin: true,
    testid: "admin-card-merge",
  },
  {
    to: "/admin/copf-roles", icon: ShieldCheck,
    title: "Gestion des rôles COPF",
    desc: "Attribuer / révoquer le rôle COPF (admin strict)",
    strictAdmin: true,
    testid: "admin-card-copf-roles",
  },
  {
    to: "/admin/legacy-migration", icon: Wand2,
    title: "Migration liens legacy",
    desc: "Outil de migration technique — usage exceptionnel",
    testid: "admin-card-legacy",
  },
];

// ---------------------------------------------------------------------------
// Composant carte
// ---------------------------------------------------------------------------
function AdminCard({ to, icon: Icon, title, desc, color, badge, testid, large, compact }) {
  const accentColor = color || "hsl(var(--aito-lagoon))";
  const iconBox = large ? "h-12 w-12" : compact ? "h-8 w-8" : "h-10 w-10";
  const iconSize = large ? "h-6 w-6" : compact ? "h-4 w-4" : "h-5 w-5";
  const titleClass = large
    ? "font-display font-bold text-base break-words [overflow-wrap:anywhere]"
    : compact
      ? "font-display font-semibold text-sm leading-snug break-words [overflow-wrap:anywhere]"
      : "font-display font-bold break-words [overflow-wrap:anywhere]";
  return (
    <Link to={to} data-testid={testid} className="block group h-full">
      <Card className={`${large ? "p-5" : compact ? "p-3" : "p-4"} h-full transition ${badge > 0 ? "border-2 border-[hsl(var(--aito-energy))]/70 bg-[hsl(var(--aito-energy))]/10 shadow-sm" : "hover:border-[hsl(var(--aito-lagoon))]"}`}>
        <div className={`flex items-start gap-3 ${compact ? "gap-2" : ""}`}>
          <div
            className={`${iconBox} rounded-md flex items-center justify-center shrink-0`}
            style={{ backgroundColor: `${accentColor}20`, color: accentColor }}
          >
            <Icon className={iconSize} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={titleClass}>{title}</span>
              {badge > 0 && (
                <Badge
                  className={`${large ? "text-xs" : "text-[10px]"} font-bold bg-[hsl(var(--aito-energy))] text-white hover:bg-[hsl(var(--aito-energy))]`}
                  data-testid={`${testid}-badge`}
                >
                  {badge > 99 ? "99+" : badge} à traiter
                </Badge>
              )}
            </div>
            {desc && !compact && (
              <div className="text-xs text-muted-foreground mt-1">{desc}</div>
            )}
          </div>
          {!compact && <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-0.5 transition shrink-0 self-center" />}
        </div>
      </Card>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function AdminPage() {
  const { user } = useAuth();
  const [pending, setPending] = useState(null);
  const [loadingPending, setLoadingPending] = useState(true);
  const [legalTexts, setLegalTexts] = useState([]);
  const [editing, setEditing] = useState(null);

  const roles = user?.roles || [];
  const isAdmin = roles.some((r) => ["admin", "copf"].includes(r));
  const isStrictAdmin = roles.includes("admin");

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/stats/pending");
        setPending(data);
      } catch (e) {
        setPending(null);
      } finally {
        setLoadingPending(false);
      }
    })();
    api.get("/legal").then((r) => setLegalTexts(r.data.items || [])).catch(() => {});
  }, [isAdmin]);

  /** Charge le contenu complet d'une rubrique avant édition. */
  const startEditing = async (lt) => {
    if (lt.status === "missing") {
      setEditing({ ...lt, title: lt.title || "", content: "" });
      return;
    }
    try {
      // L'endpoint admin renvoie le document complet (y compris content).
      const { data } = await api.get(`/admin/legal-texts/${lt.key}`);
      setEditing({ ...lt, ...data, content: data.content || "" });
    } catch (e) {
      // Si l'admin GET échoue, on bascule en création.
      setEditing({ ...lt, content: lt.content || "" });
    }
  };

  const save = async () => {
    if (!editing) return;
    try {
      // Étape 1 : enregistrer un brouillon (route admin, PUT)
      await api.put(`/admin/legal-texts/${editing.key}/draft`, {
        title: editing.title,
        content: editing.content,
      });
      // Étape 2 : publier (POST). Le backend bloque la publication si le
      // contenu contient encore un marqueur de travail ([À COMPLÉTER],
      // TODO, BROUILLON, [À CONFIRMER]) ou si le contenu est vide.
      await api.post(`/admin/legal-texts/${editing.key}/publish`);
      toast.success("Texte légal publié (nouvelle version créée).");
      setEditing(null);
      const r = await api.get("/legal");
      setLegalTexts(r.data.items || []);
    } catch (e) {
      // 422 = garde-fou de publication (marqueur de travail présent).
      // Toute autre erreur est présentée telle quelle.
      showError(e, { prefix: "Erreur :" });
    }
  };

  /** Enregistre le brouillon sans publier. */
  const saveDraft = async () => {
    if (!editing) return;
    try {
      await api.put(`/admin/legal-texts/${editing.key}/draft`, {
        title: editing.title,
        content: editing.content,
      });
      toast.success("Brouillon enregistré (non publié).");
      const r = await api.get("/legal");
      setLegalTexts(r.data.items || []);
    } catch (e) {
      showError(e, { prefix: "Erreur :" });
    }
  };

  /** Nettoyage des doublons : plusieurs documents actifs pour la même clé.
   *  Garde le plus récent (priorité au publié), désactive les autres sans
   *  rien supprimer. Les preuves de consentement restent intactes. */
  const [deduping, setDeduping] = useState(false);
  const dedupe = async () => {
    const dup = legalTexts.reduce((acc, lt) => {
      acc[lt.key] = (acc[lt.key] || 0) + 1;
      return acc;
    }, {});
    const nbDoublons = Object.values(dup).filter((n) => n > 1).length;
    const msg = nbDoublons > 0
      ? `Nettoyer ${nbDoublons} clé(s) avec doublons ? Les anciens documents resteront en base (désactivés) mais ne s'afficheront plus.`
      : "Aucun doublon détecté dans la liste visible. Lancer tout de même la vérification complète ?";
    if (!window.confirm(msg)) return;
    setDeduping(true);
    try {
      const { data } = await api.post("/admin/legal-texts/dedupe");
      if (data.deactivated_count > 0) {
        toast.success(
          `${data.deactivated_count} doublon(s) nettoyé(s). Il reste ${data.total_active_after} texte(s) actif(s).`
        );
      } else {
        toast.success("Aucun doublon à nettoyer.");
      }
      const r = await api.get("/legal");
      setLegalTexts(r.data.items || []);
    } catch (e) {
      showError(e, { prefix: "Erreur :" });
    } finally {
      setDeduping(false);
    }
  };

  if (!user) return (
    <div className="p-8">
      <Card className="p-6 text-center">Connecte-toi pour accéder à l'admin.</Card>
    </div>
  );
  if (!isAdmin) return (
    <div className="mx-auto max-w-3xl p-8">
      <Card className="p-6">
        <ShieldAlert className="h-6 w-6 text-[hsl(var(--aito-energy))]" />
        <h2 className="font-display text-xl font-bold mt-2">Accès administrateur</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Ton compte n'a pas les droits administrateur. Contacte <a href={adminMailto({ subject: "Demande d'accès Admin/COPF" })} className="underline">{getAdminEmail()}</a> pour demander un accès admin / COPF.
        </p>
      </Card>
    </div>
  );

  const totalPending = pending?.total || 0;

  return (
    <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-6" data-testid="admin-page">
      {/* En-tête */}
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold flex items-center gap-3">
          <Settings className="h-7 w-7 text-[hsl(var(--aito-lagoon))]" />
          Espace administrateur
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Bonjour {user?.name?.split(" ")[0] || "Admin"} — voici votre centre de pilotage.
          {totalPending > 0 && (
            <span className="ml-2 text-[hsl(var(--aito-energy))] font-medium">
              {totalPending} action{totalPending > 1 ? "s" : ""} à traiter aujourd'hui.
            </span>
          )}
        </p>
      </div>

      {/* Groupe 1 — À TRAITER (compteurs en avant-plan, plus grandes cartes) */}
      <section aria-labelledby="admin-section-todo" className="mb-10">
        <h2
          id="admin-section-todo"
          className="text-sm uppercase tracking-wide text-foreground mb-4 font-bold flex items-center gap-2"
        >
          <ClipboardCheck className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" /> À traiter aujourd'hui
        </h2>
        {loadingPending ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-28" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CARDS_TODO.map((c) => (
              <AdminCard
                key={c.to}
                {...c}
                large
                badge={pending?.[c.badgeKey] || 0}
              />
            ))}
          </div>
        )}
      </section>

      {/* Groupe 2 — GESTION (compacte) */}
      <section aria-labelledby="admin-section-entities" className="mb-6">
        <h2
          id="admin-section-entities"
          className="text-xs uppercase tracking-wide text-muted-foreground mb-3 font-semibold flex items-center gap-2"
        >
          <Users className="h-3.5 w-3.5" /> Gérer — Personnes & structures
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {CARDS_ENTITIES.map((c) => (
            <AdminCard key={c.to} {...c} compact />
          ))}
        </div>
      </section>

      {/* Groupe 3 — CONTENUS & ÉDITORIAL (compacte) */}
      <section aria-labelledby="admin-section-contents" className="mb-6">
        <h2
          id="admin-section-contents"
          className="text-xs uppercase tracking-wide text-muted-foreground mb-3 font-semibold flex items-center gap-2"
        >
          <Newspaper className="h-3.5 w-3.5" /> Éditorial — Contenus & mise en avant
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-2">
          {CARDS_CONTENTS.map((c) => (
            <AdminCard key={c.to} {...c} compact />
          ))}
        </div>
      </section>

      {/* Groupe 4 — OUTILS TECHNIQUES (repliable — usage occasionnel) */}
      <section className="mb-8">
        <Accordion type="single" collapsible>
          <AccordionItem value="tools" className="border rounded-lg px-3">
            <AccordionTrigger className="text-xs uppercase tracking-wide text-muted-foreground font-semibold py-3">
              <span className="flex items-center gap-2">
                <Wand2 className="h-3.5 w-3.5" /> Outils techniques & maintenance
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 pt-2 pb-3">
                {CARDS_TOOLS.filter((c) => !c.strictAdmin || isStrictAdmin).map((c) => (
                  <AdminCard key={c.to} {...c} compact />
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>

      {/* Textes légaux (repliable) */}
      <section>
        <Accordion type="single" collapsible>
          <AccordionItem value="legal" className="border rounded-lg px-3">
            <AccordionTrigger className="text-xs uppercase tracking-wide text-muted-foreground font-semibold py-3">
              <span className="flex items-center gap-2">
                <ShieldCheck className="h-3.5 w-3.5" /> Textes légaux
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <Tabs defaultValue="legal" className="mt-2">
                <TabsList>
                  <TabsTrigger value="legal" data-testid="admin-tab-legal">Textes légaux</TabsTrigger>
                  <TabsTrigger value="reports" data-testid="admin-tab-reports">Signalements</TabsTrigger>
                </TabsList>
                <TabsContent value="legal" className="mt-4 grid lg:grid-cols-12 gap-4">
                  <div className="lg:col-span-4 space-y-2">
                    {(() => {
                      // Doublons visibles = plusieurs items avec la même clé
                      const counts = legalTexts.reduce((a, lt) => { a[lt.key] = (a[lt.key] || 0) + 1; return a; }, {});
                      const dupKeys = Object.entries(counts).filter(([, n]) => n > 1).map(([k]) => k);
                      return (
                        <div className="rounded-md border bg-muted/40 p-3 mb-2" data-testid="admin-legal-dedupe-panel">
                          {dupKeys.length > 0 ? (
                            <p className="text-xs text-[hsl(var(--aito-energy))] mb-2">
                              <strong>{dupKeys.length}</strong> rubrique(s) avec doublons détectée(s) :
                              <span className="font-mono"> {dupKeys.join(", ")}</span>
                            </p>
                          ) : (
                            <p className="text-xs text-muted-foreground mb-2">Aucun doublon visible dans la liste.</p>
                          )}
                          <Button
                            onClick={dedupe}
                            disabled={deduping}
                            variant={dupKeys.length > 0 ? "default" : "outline"}
                            size="sm"
                            className={dupKeys.length > 0 ? "bg-[hsl(var(--aito-energy))] text-white hover:opacity-90 w-full" : "w-full"}
                            data-testid="admin-legal-dedupe-btn"
                          >
                            {deduping ? "Nettoyage en cours…" : "Nettoyer les doublons"}
                          </Button>
                          <p className="text-[10px] text-muted-foreground mt-2 leading-snug">
                            Désactive les anciens documents sans rien supprimer. Les preuves d'acceptation restent valides.
                          </p>
                        </div>
                      );
                    })()}
                    {legalTexts.map((lt) => (
                      <button
                        key={lt.key}
                        data-testid={`admin-legal-${lt.key}`}
                        onClick={() => startEditing(lt)}
                        className={`w-full text-left p-3 rounded-lg border ${
                          editing?.key === lt.key ? "bg-muted border-[hsl(var(--aito-lagoon))]" : "hover:bg-muted"
                        } ${lt.status === "missing" ? "border-dashed" : ""}`}
                      >
                        <div className="font-display font-semibold text-sm flex items-center gap-2">
                          {lt.title}
                          {lt.status === "missing" && (
                            <Badge variant="outline" className="text-[10px] border-[hsl(var(--aito-energy))] text-[hsl(var(--aito-energy))]">
                              À créer
                            </Badge>
                          )}
                          {lt.status === "draft" && (
                            <Badge variant="outline" className="text-[10px]">Brouillon</Badge>
                          )}
                          {lt.status === "published" && (
                            <Badge className="text-[10px] bg-[hsl(var(--aito-lagoon))] text-black">Publié</Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground font-mono">
                          {lt.key}{lt.version ? ` · v${lt.version}` : ""}
                        </div>
                      </button>
                    ))}
                  </div>
                  <Card className="lg:col-span-8 p-5">
                    {!editing ? (
                      <p className="text-sm text-muted-foreground">Sélectionne un texte à modifier ou à créer.</p>
                    ) : (
                      <div className="space-y-3">
                        {editing.status === "missing" && (
                          <div className="rounded-md bg-[hsl(var(--aito-energy)/0.08)] border border-[hsl(var(--aito-energy))] p-3 text-xs" data-testid="admin-legal-missing-hint">
                            Cette rubrique n'existe pas encore en base. En cliquant sur <strong>Enregistrer brouillon</strong>, elle sera créée pour la clé <code>{editing.key}</code>. Elle restera invisible du public tant qu'elle n'est pas publiée.
                          </div>
                        )}
                        <div>
                          <label className="text-xs font-medium">Titre</label>
                          <Input
                            data-testid="admin-legal-title"
                            value={editing.title}
                            onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium">Contenu</label>
                          <Textarea
                            data-testid="admin-legal-content"
                            rows={14}
                            value={editing.content || ""}
                            onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                            placeholder={editing.status === "missing" ? "Rédige ou colle ici le contenu de la rubrique…" : ""}
                          />
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            onClick={saveDraft}
                            variant="outline"
                            data-testid="admin-legal-save-draft"
                          >
                            {editing.status === "missing" ? "Créer la rubrique (brouillon)" : "Enregistrer brouillon"}
                          </Button>
                          <Button
                            onClick={save}
                            className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"
                            data-testid="admin-legal-save"
                          >
                            Publier nouvelle version
                          </Button>
                          <Button variant="ghost" onClick={() => setEditing(null)}>Annuler</Button>
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          La publication est automatiquement bloquée si le contenu
                          contient encore un marqueur de travail
                          (<code>[À COMPLÉTER]</code>, <code>[À CONFIRMER]</code>,
                          <code>TODO</code>, <code>BROUILLON</code>) ou s'il est
                          vide. Dans ce cas, enregistre d'abord un brouillon,
                          corrige, puis publie.
                        </p>
                      </div>
                    )}
                  </Card>
                </TabsContent>
                <TabsContent value="reports" className="mt-4">
                  <Card className="p-5 text-sm text-muted-foreground">
                    Module signalements à venir (V1.1). En attendant, les contenus signalés peuvent être adressés à <a href={adminMailto({ subject: "Signalement contenu AITO.PF" })} className="underline">{getAdminEmail()}</a>.
                  </Card>
                </TabsContent>
              </Tabs>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>
    </div>
  );
}
